import React, { useEffect, useState } from "react";
import NotificationsIcon from "@mui/icons-material/Notifications";
import { useNavigate } from "react-router-dom";
import CloseIcon from "@mui/icons-material/Close";

import httpClient from "../../../_util/api";

const NOTIFICATION_CACHE_MS = 60 * 1000;
const NOTIFICATION_LIMIT = 40;
const notificationCache = new Map();

function readStoredFlag(key) {
  try {
    return JSON.parse(localStorage.getItem(key)) === true;
  } catch {
    return false;
  }
}

function notificationCacheKey(isAdmin, isFinanceUser, agentCode) {
  const session = localStorage.getItem("authToken") || "";
  const role = isAdmin
    ? "admin"
    : isFinanceUser
      ? "finance"
      : `agent:${agentCode || ""}`;
  return `${role}:${session.slice(-16)}`;
}

function toNotification(item) {
  return {
    id: item._id,
    message: item.message,
    policyNumber: item.policyNumber || "",
    status: item.status,
    unRead: item.unRead,
    newAgentId: item.newAgentId || "",
    commissionId: item.commissionId || item.commission_id || "",
    isPaidOut: Boolean(item.isPaidOut),
  };
}

function notificationDestination(item, isAdmin, isFinanceUser) {
  if (item.newAgentId) {
    return `/addNewRecruit/${item.newAgentId}`;
  }
  if (isFinanceUser) {
    return item.commissionId
      ? `/commissionDetail/${item.commissionId}`
      : "/commissions";
  }
  if (item.policyNumber) {
    const encoded = encodeURIComponent(item.policyNumber);
    return isAdmin
      ? `/approvePolicyByPolicyNumber/${encoded}`
      : `/policyDetail/${encoded}`;
  }
  return "";
}

function DuplicateNotification() {
  const agentCode = localStorage.getItem("agentCode");
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [noOfUnReadNotifications, setNoOfUnReadNotifications] = useState(0);
  const [listTruncated, setListTruncated] = useState(false);

  const isAdmin = readStoredFlag("isAdmin");
  const isFinanceUser = readStoredFlag("isFinanceUser");

  const handleOpen = () => {
    getNotifications(true);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const markReadLocally = (id) => {
    const key = notificationCacheKey(isAdmin, isFinanceUser, agentCode);
    const cached = notificationCache.get(key);
    const target = (cached?.notifications || notifications).find(
      (item) => item.id === id,
    );
    if (!target?.unRead) return;

    const nextNotifications = (cached?.notifications || notifications).map(
      (item) => (item.id === id ? { ...item, unRead: false } : item),
    );
    const unread = Math.max(0, (cached?.unread ?? noOfUnReadNotifications) - 1);
    notificationCache.set(key, {
      at: Date.now(),
      notifications: nextNotifications,
      unread,
      truncated: Boolean(cached?.truncated ?? listTruncated),
    });
    setNotifications(nextNotifications);
    setNoOfUnReadNotifications(unread);
  };

  const handleOptionChange = (item) => {
    if (item?.id) {
      httpClient.post(`/notifications/updateNotification/${item.id}`).catch((error) => {
        console.log(error);
      });
      markReadLocally(item.id);
    }

    const destination = notificationDestination(item, isAdmin, isFinanceUser);
    if (!destination) return;

    setOpen(false);
    navigate(destination);
  };

  const applyNotifications = (key, list, unread) => {
    const visible = (list || []).slice(0, NOTIFICATION_LIMIT).map(toNotification);
    notificationCache.set(key, {
      at: Date.now(),
      notifications: visible,
      unread,
      truncated: (list || []).length > NOTIFICATION_LIMIT,
    });
    setNotifications(visible);
    setNoOfUnReadNotifications(unread || 0);
    setListTruncated((list || []).length > NOTIFICATION_LIMIT);
  };

  const getNotifications = async (force = false) => {
    const key = notificationCacheKey(isAdmin, isFinanceUser, agentCode);
    const cached = notificationCache.get(key);
    if (!force && cached && Date.now() - cached.at < NOTIFICATION_CACHE_MS) {
      setNotifications(cached.notifications);
      setNoOfUnReadNotifications(cached.unread);
      setListTruncated(Boolean(cached.truncated));
      return;
    }

    if (!isAdmin && !isFinanceUser && !agentCode) return;

    const url = isAdmin
      ? "/notifications/getAllNotifications_AdminView"
      : isFinanceUser
        ? "/notifications/getAllNotifications_FinanceView"
        : `/notifications/getAllNotifications_AgentView/${encodeURIComponent(agentCode)}`;

    const res = await httpClient.get(url).catch((error) => {
      console.log(error);
    });

    if (res?.status === 200) {
      applyNotifications(
        key,
        res.data.notifications,
        res.data.noOfUnReadNotification,
      );
    }
  };

  const deleteNotification = async (id) => {
    const res = await httpClient
      .delete(`/notifications/delete/${id}`)
      .catch((error) => {
        console.log(error);
      });
    if (res?.status === 200) {
      const key = notificationCacheKey(isAdmin, isFinanceUser, agentCode);
      const removed = notifications.find((item) => item.id === id);
      const nextNotifications = notifications.filter((item) => item.id !== id);
      const unread = removed?.unRead
        ? Math.max(0, noOfUnReadNotifications - 1)
        : noOfUnReadNotifications;
      notificationCache.set(key, {
        at: Date.now(),
        notifications: nextNotifications,
        unread,
        truncated: listTruncated,
      });
      setNotifications(nextNotifications);
      setNoOfUnReadNotifications(unread);
    }
  };
  const deleteNotifications = async () => {
    const res = await httpClient
      .delete(`/notifications/delete`)
      .catch((error) => {
        console.log(error);
      });
    if (res?.status === 200) {
      notificationCache.delete(
        notificationCacheKey(isAdmin, isFinanceUser, agentCode),
      );
      setNotifications([]);
      setNoOfUnReadNotifications(0);
      setOpen(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(getNotifications, 900);
    return () => clearTimeout(timer);
  }, []);
  return (
    <>
      <div className="">
        <div
          className="h-10 w-10 rounded-full flex items-center justify-center"
          style={{
            background: "#fff",
            height: "40px",
            width: "40px",
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            marginRight: "10px",
            position: "relative",
            top: 0,
            left: 0,
          }}
          onClick={handleOpen}
        >
          <NotificationsIcon sx={{ color: "black", fontSize: "25px" }} />
          {noOfUnReadNotifications > 0 && (
            <p
              style={{
                height: "20px",
                minWidth: "20px",
                padding: "0 4px",
                background: "#F08613",
                position: "absolute",
                top: "-5px",
                right: "-5px",
                borderRadius: "10px",
                color: "#fff",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                fontSize: "10px",
              }}
            >
              {noOfUnReadNotifications > 99 ? "99+" : noOfUnReadNotifications}
            </p>
          )}
        </div>
      </div>

      {open && (
        <div className="notification-popUp">
          <div className="notification-container">
            <p style={{ color: "#111", fontSize: "30px", fontWeight: 600 }}>
              NOTIFICATION
            </p>

            <div className="notification-content-container hide-scrollbar">
              {listTruncated && (
                <p style={{ color: "#111", fontSize: "13px", textAlign: "center" }}>
                  Showing the {notifications.length} most recent notifications.
                </p>
              )}
              {notifications?.length > 0 ? (
                notifications.map((item, index) => {
                  return (
                    <div
                      className={`${item.unRead ? "unread" : "read"}`}
                      key={index}
                    >
                      <p className={`notificationText`}>{item.message}</p>

                      <button
                        className={`readButton ${item.unRead && "unreadbtn"}`}
                        onClick={() => handleOptionChange(item)}
                      >
                        Read
                      </button>
                      <button
                        className="deleteButton"
                        onClick={() => deleteNotification(item.id)}
                      >
                        Delete
                      </button>
                    </div>
                  );
                })
              ) : (
                <p
                  style={{
                    color: "#111",
                    fontSize: "24px",
                    textAlign: "center",
                    fontWeight: "bold",
                  }}
                >
                  No Notifications Available
                </p>
              )}
            </div>

            {notifications.length > 0 && (
              <button
                className="clearBtn"
                onClick={() => {
                  deleteNotifications();
                }}
              >
                Clear All Notification
              </button>
            )}
            <div className="notification-close" onClick={handleClose}>
              <CloseIcon sx={{ color: "black", fontSize: "25px" }} />
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default DuplicateNotification;

// https://partnerlink.jhancock.com/

//       $or: [{ newPolicy: true }, { newAgent: true }],
